import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { rememberOffer } from '../../utils/offerSession';
import { ctaLabel, endsLabel, offerPath, offerStyle } from './offerFormat';
import { track } from '../../utils/tracker';

const FONT = '"DM Sans", system-ui, sans-serif';

/** Shown over the homepage hero while an offer slide (Coupons → "Hero banner slide") is on screen. */
export default function OfferHeroCard({ offer }) {
  const navigate = useNavigate();
  if (!offer) return null;
  const style = offerStyle(offer);
  const go = () => {
    rememberOffer(offer.code);
    track('offer_click', { code: offer.code, place: 'hero' });
    navigate(offerPath(offer));
  };

  return (
    <>
      {/* Desktop: card top-right, clear of the headline (left) and stats (bottom-right) */}
      <Box sx={{
        display: { xs: 'none', md: 'block' }, position: 'absolute', top: 150, right: '2.5rem', zIndex: 3, width: 320,
        borderRadius: '18px', overflow: 'hidden', background: 'rgba(8,16,32,0.78)', backdropFilter: 'blur(18px) saturate(1.4)',
        border: '1px solid rgba(196,154,60,0.3)', color: '#fff', fontFamily: FONT, animation: 'offerHeroIn .5s ease-out',
        '@keyframes offerHeroIn': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'none' } },
      }}>
        <Box sx={{ px: 2.5, pt: 2, pb: 2.2 }}>
          <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: style.ink, fontFamily: FONT }}>
            {offer.theme?.eyebrow || 'Offer'}{offer.endDate ? ` · ${endsLabel(offer.endDate)}` : ''}
          </Typography>
          <Typography sx={{ mt: 0.6, fontFamily: '"Playfair Display", Georgia, serif', fontSize: '1.35rem', fontWeight: 600, lineHeight: 1.2 }}>
            {offer.title || offer.discountLabel}
          </Typography>
          <Typography sx={{ mt: 0.6, fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', fontFamily: FONT }}>
            {offer.rules?.[0]}
          </Typography>
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box component="span" sx={{ px: 1, py: 0.4, borderRadius: '8px', bgcolor: '#FFBD59', color: '#0D1F3C', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.1em' }}>
              {offer.code}
            </Box>
            <Box component="button" type="button" onClick={go}
              sx={{ flex: 1, border: 'none', cursor: 'pointer', borderRadius: '10px', bgcolor: '#fff', color: '#0D1F3C', py: 0.9, fontWeight: 700, fontSize: '0.82rem', fontFamily: FONT, '&:hover': { bgcolor: '#f3f4f6' } }}>
              {ctaLabel(offer)} →
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Mobile: one-line pill */}
      <Box component="button" type="button" onClick={go} sx={{
        display: { xs: 'flex', md: 'none' }, position: 'absolute', top: 132, left: '1.25rem', right: '1.25rem', zIndex: 3,
        alignItems: 'center', gap: 1, px: 1.5, py: 1, border: '1px solid rgba(196,154,60,0.35)', borderRadius: '999px',
        background: 'rgba(8,16,32,0.8)', backdropFilter: 'blur(14px)', color: '#fff', fontFamily: FONT, fontSize: '0.78rem', cursor: 'pointer', textAlign: 'left',
      }}>
        <Box component="span" sx={{ px: 0.8, py: 0.2, borderRadius: '6px', bgcolor: '#FFBD59', color: '#0D1F3C', fontWeight: 800, fontSize: '0.68rem', letterSpacing: '0.08em', flexShrink: 0 }}>{offer.code}</Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{offer.title || offer.discountLabel}</Box>
        <Box component="span" sx={{ flexShrink: 0, fontWeight: 700 }}>→</Box>
      </Box>
    </>
  );
}
